import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronDown, LogOut, User, Menu } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { BodyHugeBold } from "../ui/Typography";

interface HeaderProps {
  onMenuClick?: () => void;
}

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const getPageTitle = () => {
    const path = location.pathname;
    switch (path) {
      case "/customers":
        return "Customers";
      case "/daily-entries":
        return "Daily Entries";
      case "/chips":
        return "Chips";
      case "/cash-in":
        return "Cash In";
      case "/cash-out":
        return "Cash Out";
      case "/agents":
        return "Agents";
      case "/countries":
        return "Countries";
      case "/roles":
        return "Roles and Permissions";
      case "/staffs":
        return "Staffs";
      case "/surveillance":
        return "Surveillance";
      case "/cash-records":
        return "Cash Records";
      case "/games":
        return "Games";
      case "/game-records":
        return "Game Records";
      case "/game-tables":
        return "Game Tables";
      case "/blacklisted-players":
        return "Blacklisted Players";
      case "/menu-bar":
        return "Navigation Menu";
      case "/audit-logs":
        return "Audit Logs";
      default:
        if (path.startsWith("/customers/")) {
          return "Customer Detail";
        }
        if (path.startsWith("/countries/")) {
          return "States";
        }
        return "-";
    }
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
    setIsDropdownOpen(false);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const getUserInitials = () => {
    if (user?.name) {
      return user.name
        .split(" ")
        .map((name) => name.charAt(0))
        .join("")
        .toUpperCase()
        .slice(0, 2);
    }
    return "U";
  };

  return (
    <header className="bg-background border-b border-slate-700 px-4 lg:px-8 py-4">
      <div className="flex items-center justify-between">
        {/* Mobile Menu Button and Page Title Section */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuClick}
            className="lg:hidden text-slate-400 hover:text-white transition-colors"
            aria-label="Toggle menu"
          >
            <Menu size={24} />
          </button>
          <BodyHugeBold className="text-yellow-400">
            {getPageTitle()}
          </BodyHugeBold>
        </div>

        {/* Profile Section */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={toggleDropdown}
            className="flex items-center space-x-3 p-2 rounded-lg hover:bg-slate-800 transition-colors"
          >
            {/* Profile Avatar */}
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-sm">
              {getUserInitials()}
            </div>

            <ChevronDown
              className={`w-4 h-4 text-slate-400 transition-transform ${
                isDropdownOpen ? "rotate-180" : ""
              }`}
            />
          </button>

          {/* Dropdown Menu */}
          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-slate-800 rounded-lg shadow-lg border border-slate-700 py-2 z-50">
              <button
                className="w-full flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
                onClick={() => {
                  // TODO: Navigate to profile page
                  console.log("Profile clicked");
                  setIsDropdownOpen(false);
                }}
              >
                <User className="w-4 h-4 mr-3" />
                Profile
              </button>

              <div className="border-t border-slate-700 my-2"></div>

              <button
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2 text-sm text-slate-300 hover:bg-slate-700 hover:text-white transition-colors"
              >
                <LogOut className="w-4 h-4 mr-3" />
                Logout
              </button>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
